import Button from "@material-ui/core/Button";
import DialogContentText from "@material-ui/core/DialogContentText";
import ActionDialog from "@saleor/components/ActionDialog";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import UploadErrorDialog from "@saleor/components/UploadErrorDialog";
import { WindowTitle } from "@saleor/components/WindowTitle";
import {
  DEFAULT_INITIAL_PAGINATION_DATA,
  DEFAULT_INITIAL_SEARCH_DATA
} from "@saleor/config";
import messages from "@saleor/containers/BackgroundTasks/messages";
import { Task } from "@saleor/containers/BackgroundTasks/types";
import SalesBulkUploadDialog from "@saleor/discounts/components/SalesBulkUploadDialog";
import useSaleUserType from "@saleor/discounts/hooks/useSaleUserType";
import getSaleAddUrlByUserType from "@saleor/discounts/utils/getSaleAddUrlByUserType";
import getSaleUpdateUrlByUserType from "@saleor/discounts/utils/getSaleUpdateUrlByUserType";
import useBackgroundTask from "@saleor/hooks/useBackgroundTask";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { commonMessages, sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import useBrandUpdate from "@saleor/products/hooks/useBrandUpdate";
import { useProductUpload } from "@saleor/products/mutations";
import useBrandPrivateSearch from "@saleor/searches/useBrandPrivateSearch";
import { ListViews } from "@saleor/types";
import {
  ProductUploadTemplateTypeEnum,
  ProductUserViewEnum
} from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { getSortParams } from "@saleor/utils/sort";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import SaleListPage from "../../components/SaleListPage";
import { TypedsaleBulkDeactivate } from "../../mutations";
import { useSaleListQuery } from "../../queries";
import { SaleBulkDeactivate } from "../../types/SaleBulkDeactivate";
import {
  saleListUrl,
  SaleListUrlDialog,
  SaleListUrlQueryParams
} from "../../urls";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";
import { getSortQueryVariables } from "./sort";
interface SaleListProps {
  params: SaleListUrlQueryParams;
}

export const SaleList: React.FC<SaleListProps> = ({ params }) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const { queue } = useBackgroundTask();
  const paginate = usePaginator();
  const shop = useShop();
  const [uploadLoading, setUploadLoading] = React.useState(false);
  const [uploadErrorLink, setUploadErrorLink] = React.useState(null);
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );

  const { updateListSettings, settings } = useListSettings(
    ListViews.SALES_LIST
  );
  const intl = useIntl();

  const saleUser = useSaleUserType();

  const { result: brandsResult } = useBrandPrivateSearch({
    skip: !saleUser.isKAMS,
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      accountManager: saleUser.id
    }
  });

  const brands = maybe(() => brandsResult?.data?.search?.edges, []).map(
    edge => edge.node
  );

  // Hook for updating of brands
  const [onBrandChange, brand, brandChoices] = useBrandUpdate(
    params,
    brands,
    brandsResult.loading
  );

  const paginationState = createPaginationState(settings.rowNumber, params);
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: {
        ...getFilterVariables(params),
        ...(saleUser.isKAMS && {
          brand: brand.value
        })
      },
      sort: getSortQueryVariables(params)
    }),
    [params]
  );
  const { data, loading, refetch } = useSaleListQuery({
    displayLoader: true,
    variables: queryVariables
  });

  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    cleanupFn: reset,
    createUrl: saleListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    SaleListUrlDialog,
    SaleListUrlQueryParams
  >(navigate, saleListUrl, params);

  const handleTabChange = (tab: number) => {
    reset();
    navigate(
      saleListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    reset();
    navigate(saleListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };

  const canOpenBulkActionDialog = maybe(() => params.ids.length > 0);

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.sales.pageInfo),
    paginationState,
    params
  );

  const handleSaleBulkDeactivate = (data: SaleBulkDeactivate) => {
    if (data.saleBulkDeactivate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Sales Deactivated."
        })
      });
      reset();
      closeModal();
      refetch();
    } else if (data.saleBulkDeactivate.errors.length !== 0) {
      closeModal();
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Unable to deactivate. Sales is ongoing."
        })
      });
      reset();
      refetch();
    }
  };

  const handleSort = createSortHandler(navigate, saleListUrl, params);
  const currencySymbol = maybe(() => shop.defaultCurrency, "PHP");

  React.useEffect(
    () =>
      navigate(
        saleListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  const [uploadProduct, uploadProductOpts] = useProductUpload({
    onCompleted: data => {
      if (data.productUploadTemplate.productErrors.length === 0) {
        queue(Task.UPLOAD, {
          id: data.productUploadTemplate.productUpload.id,
          onResponseData: jobData => {
            if (jobData && jobData.data) {
              if (jobData.data.productUploadJob.status === "WITH_ERROR_FILE") {
                notify({
                  status: "error",
                  text: intl.formatMessage(commonMessages.uploadError),
                  title: intl.formatMessage(messages.uploadFailedTitle)
                });
                setUploadErrorLink(
                  jobData.data?.productUploadJob?.errorFileUrl
                );
              }
              if (jobData.data.productUploadJob.status === "EMPTY_FILE") {
                notify({
                  status: "error",
                  text: intl.formatMessage(commonMessages.uploadEmptyError),
                  title: intl.formatMessage(messages.uploadFailedTitle)
                });
              }
              if (jobData.data.productUploadJob.status !== "PENDING") {
                setUploadLoading(false);
                closeModal();
                reset();
              }
            } else {
              setUploadLoading(false);
              closeModal();
              reset();
              notify({
                status: "error",
                text: intl.formatMessage(commonMessages.somethingWentWrong),
                title: intl.formatMessage(messages.uploadFailedTitle)
              });
            }
          }
        });
      }
    }
  });

  React.useEffect(() => {
    if (saleUser.isKAMS) {
      navigate(
        saleListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA,
          brand: brand.value
        })
      );
    }
  }, [brand.value]);

  return (
    <TypedsaleBulkDeactivate onCompleted={handleSaleBulkDeactivate}>
      {(saleBulkDeactivate, saleBulkDeactivateOpts) => {
        const onSaleBulkDeactivate = () =>
          saleBulkDeactivate({
            variables: {
              ids: params.ids
            }
          });

        return (
          <>
            <WindowTitle title={intl.formatMessage(sectionNames.sales)} />
            <SaleListPage
              isKAMS={saleUser.isKAMS}
              brand={brand.value}
              kamsBrands={brandChoices}
              brandLabel={brand.label}
              onBrandChange={onBrandChange}
              brands={null}
              currencySymbol={currencySymbol}
              currentTab={currentTab}
              filterOpts={getFilterOpts(params)}
              initialSearch={params.query || ""}
              onSearchChange={handleSearchChange}
              onFilterChange={filter => changeFilters(filter)}
              onAll={resetFilters}
              onTabChange={handleTabChange}
              onTabDelete={() => openModal("delete-search")}
              onTabSave={() => openModal("save-search")}
              tabs={tabs.map(tab => tab.name)}
              defaultCurrency={maybe(() => shop.defaultCurrency)}
              sales={maybe(() => data.sales.edges.map(edge => edge.node))}
              settings={settings}
              disabled={loading}
              pageInfo={pageInfo}
              onAdd={() => navigate(getSaleAddUrlByUserType(saleUser, params))}
              onNextPage={loadNextPage}
              onPreviousPage={loadPreviousPage}
              onSort={handleSort}
              onUpdateListSettings={updateListSettings}
              onUpload={path => openModal(path)}
              onRowClick={id => () =>
                navigate(getSaleUpdateUrlByUserType(saleUser, id, params))}
              isChecked={isSelected}
              selected={listElements.length}
              sort={getSortParams(params)}
              toggle={toggle}
              toggleAll={toggleAll}
              toolbar={
                <Button
                  color="primary"
                  onClick={() =>
                    openModal("deactivate", {
                      ids: listElements
                    })
                  }
                >
                  <FormattedMessage
                    defaultMessage="Deactivate"
                    description="deactivate button"
                  />
                </Button>
              }
            />
            <ActionDialog
              confirmButtonState={saleBulkDeactivateOpts.status}
              onClose={closeModal}
              onConfirm={onSaleBulkDeactivate}
              open={params.action === "deactivate" && canOpenBulkActionDialog}
              title={intl.formatMessage({
                defaultMessage: "Deactivate Sales",
                description: "dialog header"
              })}
              confirmButtonLabel="Deactivate"
              variant="delete"
            >
              {canOpenBulkActionDialog && (
                <DialogContentText>
                  <FormattedMessage
                    defaultMessage="{counter,plural,one{Are you sure you want to deactivate this sale?} other{Are you sure you want to deactivate {displayQuantity} sales?}}"
                    description="dialog content"
                    values={{
                      counter: params.ids.length,
                      displayQuantity: <strong>{params.ids.length}</strong>
                    }}
                  />
                </DialogContentText>
              )}
            </ActionDialog>
            <SalesBulkUploadDialog
              open={
                params.action === "uploadSalesPartnerPercentage" ||
                params.action === "uploadSalesMerchantAbsolute" ||
                params.action === "uploadSalesPartnerAbsolute" ||
                params.action === "uploadSalesMerchantPercentage"
              }
              action={params.action}
              loading={uploadProductOpts.loading || uploadLoading}
              onClose={closeModal}
              onFileError={() => {
                notify({
                  status: "error",
                  text: intl.formatMessage({
                    defaultMessage: "Please upload XLSX files only."
                  }),
                  title: intl.formatMessage({
                    defaultMessage: "Can't upload file!",
                    description: "import, header"
                  })
                });
              }}
              onSubmit={(getFile, isOverwrite) => {
                setUploadLoading(true);
                // let templateType =
                //   ProductUploadTemplateTypeEnum.SALES_MERCHANT_PERCENTAGE_PRODUCT;
                let templateType =
                  ProductUploadTemplateTypeEnum.SALES_MERCHANT_PERCENTAGE_PRODUCT;
                if (params.action === "uploadSalesMerchantAbsolute") {
                  templateType =
                    ProductUploadTemplateTypeEnum.SALES_MERCHANT_ABSOLUTE_VALUE_PRODUCT;
                } else if (params.action === "uploadSalesPartnerAbsolute") {
                  templateType =
                    ProductUploadTemplateTypeEnum.SALES_PARTNERSHIP_ABSOLUTE_VALUE_PRODUCT;
                } else if (params.action === "uploadSalesPartnerPercentage") {
                  templateType =
                    ProductUploadTemplateTypeEnum.SALES_PARTNERSHIP_PERCENTAGE_PRODUCT;
                }
                uploadProduct({
                  variables: {
                    file: getFile,
                    overwrite: isOverwrite,
                    templateType,
                    ...(saleUser.isKAMS &&
                      brand.value !== undefined && {
                        brand: brand.value,
                        uploadAs: ProductUserViewEnum.SELLER_MASTER
                      })
                  }
                });
              }}
            />
            <UploadErrorDialog
              open={uploadErrorLink}
              onClose={() => setUploadErrorLink(null)}
              uploadErrorLink={uploadErrorLink}
              loading={uploadProductOpts.loading || uploadLoading}
            />
            <SaveFilterTabDialog
              open={params.action === "save-search"}
              confirmButtonState="default"
              onClose={closeModal}
              onSubmit={handleTabSave}
            />
            <DeleteFilterTabDialog
              open={params.action === "delete-search"}
              confirmButtonState="default"
              onClose={closeModal}
              onSubmit={handleTabDelete}
              tabName={maybe(() => tabs[currentTab - 1].name, "...")}
            />
          </>
        );
      }}
    </TypedsaleBulkDeactivate>
  );
};
export default SaleList;
